// Libraries
import i18next from 'i18next';

// Utilities
import Personalizer from 'personalizer';
import Conversion from 'utils/Conversion';
import { getFormattedDistanceString, getWorkoutConfiguration, roundEstimatedTime } from './WorkoutUtil';
import { getExerciseTranslation } from 'utils/ExerciseTypesUtil';
import {
    getZoneBySportType,
    getPowerZoneCount,
    getZonesValues,
    getTargetZoneFieldString,
    isMetric,
    customPersonalizePace,
    getEffortBasedOptions
} from './TargetUtils';
import { getLinkedBenchmarkExercise, getWeightWithUnit } from 'utils/workout/BenchmarkExercisesUtil';
import ConversionFactors from 'utils/personalizer/ConversionFactors';

// Constants
import { ExecutableStepDTOType, RepeatGroupDTOType } from '../constants/WorkoutConstants';

// Constants
import Types from 'pages/workouts/util/Types';
import { StepFields } from 'WorkoutPage/constants/WorkoutStepConstants';

// Styles
import {
    ColorRedPrimary,
    ColorBluePrimary,
    ColorDarkuiAccent2,
    ColorGreenPrimary
} from 'ui-connect/styles/default-theme';

// make sure namespace(s) used in this file are loaded for translations
i18next.loadNamespaces(['units', 'workout']);

/** @typedef {import('../types/WorkoutDTO').WorkoutDTO} WorkoutDTO */
/** @typedef {import('../types/WorkoutDTO').WorkoutSegmentDTO} WorkoutSegmentDTO */
/** @typedef {import('../types/WorkoutDTO').ExecutableStepDTO} ExecutableStepDTO */

/**
 * Description: Returns the sport type string that is displayed as the step's title
 * @param {string} sportType
 * @param {ExecutableStepDTO} workoutStep
 * @returns {string}
 */
export const getStepTypeString = (sportType, workoutStep) => {
    let stepType = '';

    if (workoutStep.stepType.stepTypeKey === Types.StepTypes.Interval) {
        switch (sportType) {
            case Types.SportTypes.Running:
                stepType = i18next.t('workout:sport.type.running');
                break;
            case Types.SportTypes.Cycling:
                stepType = i18next.t('workout:sport.type.cycling');
                break;
            case Types.SportTypes.Other:
                stepType = i18next.t('workout:sport.type.other');
                break;
            case Types.SportTypes.Swimming:
                stepType = i18next.t('workout:sport.type.swim');
                break;
            case Types.SportTypes.Yoga:
            case Types.SportTypes.Strength_Training:
            case Types.SportTypes.Pilates:
            case Types.SportTypes.Cardio:
            case Types.SportTypes.Hiit:
            case Types.SportTypes.Mobility:
            default:
                stepType = i18next.t('workout:exercise_label');
                break;
        }
    } else {
        stepType = i18next.t('workout:workout.stepType.' + workoutStep.stepType.stepTypeKey);

        if (workoutStep.stepType.stepTypeKey === Types.StepTypes.Other) {
            stepType = i18next.t('workout:workout.other');
        }
    }

    return stepType;
};

/**
 * Description: Returns the hex color code that shows on the left side of the step
 *
 * @param {ExecutableStepDTO} workoutStep
 * @returns {string}
 */
export const getStepColor = (workoutStep) => {
    let color = '';

    switch (workoutStep.stepType.stepTypeKey) {
        case Types.StepTypes.Warm_Up:
            color = ColorRedPrimary;
            break;
        case Types.StepTypes.Interval:
        case Types.StepTypes.Other:
        case Types.StepTypes.Main:
            color = ColorBluePrimary;
            break;
        case Types.StepTypes.Recovery:
        case Types.StepTypes.Rest:
            color = ColorDarkuiAccent2;
            break;
        case Types.StepTypes.Cool_Down:
            color = ColorGreenPrimary;
            break;
        default:
            color = ColorDarkuiAccent2;
            break;
    }

    return color;
};

/**
 * Description: Get the fields to display for the given step
 * Backbone Reference: getStepValues() in backbone/pages/workouts/util/StepUtil.js
 *
 * @param {WorkoutSegmentDTO | WorkoutDTO} workoutData
 * @param {ExecutableStepDTO} workoutStep
 * @param {string} segmentSportType
 * @param {*} powerZone
 * @param {*} heartRateZone
 * @param {boolean} showPoolSwim2
 * @param {*} powerCurve
 * @param {*} exerciseTypes
 * @param {*} benchmarkExercises
 * @param {number} cssValue
 * @param {boolean} cssFetchFail
 * @param {boolean} showWorkoutStepMidpoints // CA-179082: cleanup ticket
 * @returns {{value: string, label: string}[]}
 */
export const getStepFields = (
    workoutData,
    workoutStep,
    segmentSportType,
    powerZone,
    heartRateZone,
    showPoolSwim2,
    powerCurve,
    exerciseTypes,
    benchmarkExercises,
    cssValue,
    cssFetchFail,
    showWorkoutStepMidpoints // CA-179082: cleanup ticket
) => {
    // CA-169978: remove showPoolSwim2 arg once the feature is released and works well
    const fields = []; // Example: Array<{ value: "200 yd", label: "Total Distance" }>
    const isViewingProviderWorkoutPage = !workoutData.workoutId && !!workoutData.workoutSourceId;
    let distanceUnitKey = Personalizer.isMetricMeasurementSystem() ? 'kilometer' : 'mile';

    // override distance unitKey for pool swim because pool swim requires step units to match poolLength unitKey
    if (workoutData.sportType.sportTypeKey === Types.SportTypes.Swimming) {
        distanceUnitKey = workoutData.poolLengthUnit?.unitKey;
    }

    if (workoutStep.preferredEndConditionUnit?.unitKey) {
        distanceUnitKey = workoutStep.preferredEndConditionUnit.unitKey;
    }

    const durationValue = getDurationValue(
        workoutStep,
        segmentSportType,
        distanceUnitKey,
        workoutData.estimatedDistanceUnit?.unitKey,
        workoutData.poolLength,
        cssValue,
        cssFetchFail,
        showPoolSwim2 // CA-169978: cleanup poolSwim2 feature flag
    );

    //for fitness duration must be added after the exercise name
    if (showExerciseField(segmentSportType, workoutStep)) {
        let exerciseValue = getExerciseTranslation({
            categoryKey: workoutStep.category,
            exerciseKey: workoutStep.exerciseName
        });

        if (!workoutStep.category && !workoutStep.exerciseName) {
            //placeholder for when no value was yet added
            exerciseValue = '--';
        }
        fields.push({ value: exerciseValue, label: i18next.t('workout:exercise_label') });
    }

    const isStrengthTrainingOrCardio =
        segmentSportType === Types.SportTypes.Strength_Training || segmentSportType === Types.SportTypes.Cardio;
    // CA-169978: Clean up poolSwim2
    const durationLabel = getDurationLabel(
        workoutStep.endCondition?.conditionTypeKey,
        isStrengthTrainingOrCardio,
        showPoolSwim2
    );
    fields.push({ value: durationValue, label: durationLabel });

    if (showStrokeType(workoutStep)) {
        // CA-169978: remove showPoolSwim2 logic once the feature is released and works well
        const strokeStringKey = getStrokeStringId(workoutStep.strokeType?.strokeTypeKey, showPoolSwim2);
        const strokeValue = i18next.t(strokeStringKey);
        const strokeLabel = i18next.t('workout:stroke_type');
        fields.push({ value: strokeValue, label: strokeLabel });
    }

    // CA-169978: remove showPoolSwim2 once the feature is released and works well
    if (showEstimateField(workoutStep, segmentSportType, showPoolSwim2)) {
        const { estValue, estLabel } = getEstimatedDuration(
            workoutStep,
            segmentSportType,
            workoutData.avgTrainingSpeed,
            distanceUnitKey,
            workoutData.poolLength,
            showPoolSwim2 // CA-169978: cleanup feature flag
        );
        fields.push({ value: estValue, label: estLabel });
    }

    if (showIntensityTargetField(workoutStep)) {
        let intensityTargetLabel = i18next.t('workout:intensity_target');
        if (showSecondaryTargetField(workoutStep)) {
            intensityTargetLabel = i18next.t('workout:primary_target');
        }

        const targetValue = getTargetValue({
            workoutStep,
            sportType: segmentSportType,
            allSportPowerZone: powerZone,
            allSportHeartRateZone: heartRateZone,
            isSecondaryTarget: false,
            powerCurve,
            poolLengthUnitKey: null, // poolSwim only uses secondaryTargetValue* because device/API use targetValue* for drill type
            cssValue: null, // poolSwim only uses secondaryTargetValue* because device/API use targetValue* for drill type
            cssFetchFail: null, // poolSwim only uses secondaryTargetValue* because device/API use targetValue* for drill type
            showWorkoutStepMidpoints // CA-179082: cleanup ticket
        }).targetValueOne;

        fields.push({ value: targetValue, label: intensityTargetLabel });
    }

    if (showSecondaryTargetField(workoutStep)) {
        // pool swim shows 'intesity target' in UI but must use secondary target for it's data due to device
        // requirement for API to put drill info in intensity target when sending to device
        // mobile/web don't mess with this logic, they just pretend secondary data is primary in UI
        const secondaryTargetLabel =
            segmentSportType === Types.SportTypes.Swimming
                ? i18next.t('workout:intensity_target')
                : i18next.t('workout:secondary_target');

        const targetValue = getTargetValue({
            workoutStep,
            sportType: segmentSportType,
            allSportPowerZone: powerZone,
            allSportHeartRateZone: heartRateZone,
            isSecondaryTarget: true,
            powerCurve,
            poolLengthUnitKey: workoutData.poolLengthUnit?.unitKey,
            cssValue,
            cssFetchFail,
            showWorkoutStepMidpoints // CA-179082: cleanup ticket
        }).targetValueOne;
        fields.push({ value: targetValue, label: secondaryTargetLabel });
    }

    // CA-169978: remove showPoolSwim2 once the feature's released and working
    if (showDrillField(workoutStep, showPoolSwim2) && !!workoutStep.drillType?.drillTypeKey) {
        const drillValue = i18next.t(`workout:workout.drill.type.${workoutStep.drillType.drillTypeKey}`);
        const drillLabel = i18next.t('workout:drill.type.label');
        fields.push({ value: drillValue, label: drillLabel });
    }

    if (showEquipmentField(workoutStep) && !!workoutStep.equipmentType?.equipmentTypeKey) {
        const equipmentValue = i18next.t(`workout:workout.equipment.${workoutStep.equipmentType.equipmentTypeKey}`);
        const equipmentLabel = i18next.t('workout:workout_equipment');
        fields.push({ value: equipmentValue, label: equipmentLabel });
    }

    if (showWeightField(segmentSportType, workoutStep, isViewingProviderWorkoutPage)) {
        const { value, label } = getWeightFieldValue(workoutStep, segmentSportType, exerciseTypes, benchmarkExercises);
        if (value) {
            fields.push({ value, label });
        }
    }

    return fields;
};

// CA-169978: remove showPoolSwim2 once the feature's released and working
const getStrokeStringId = (stringKey, showPoolSwim2) => {
    let newStringKey = stringKey;

    switch (stringKey) {
        case 'any_stroke':
            newStringKey = showPoolSwim2
                ? 'workout:workout.stroke.type.choice'
                : 'workout:workout.stoke.type.any_stroke';
            break;
        case 'free':
            newStringKey = showPoolSwim2 ? 'workout:workout.stroke.type.freestyle' : 'workout:workout.stoke.type.free';
            break;
        case 'fly':
            newStringKey = showPoolSwim2 ? 'workout:workout.stroke.type.butterfly' : 'workout:workout.stoke.type.fly';
            break;
        case 'individual_medley':
            newStringKey = showPoolSwim2
                ? 'workout:workout.stroke.type.individual_medley_IM'
                : 'workout:workout.stoke.type.individual_medley';
            break;
        case 'backstroke':
            newStringKey = 'workout:workout.stoke.type.backstroke';
            break;
        case 'breaststroke':
            newStringKey = 'workout:workout.stoke.type.breaststroke';
            break;
        case 'drill':
            newStringKey = 'workout:workout.stoke.type.drill';
            break;
        case 'mixed':
            newStringKey = 'workout:workout.stoke.type.mixed';
            break;
        case 'individual_medley_by_round':
            newStringKey = 'workout:workout.stroke.type.individual_medley_by_round';
            break;
        case 'reverse_individual_medley_by_round':
            newStringKey = 'workout:workout.stroke.type.reverse_individual_medley_by_round';
            break;
    }

    return newStringKey;
};

/**
 * Description: Get the label to display based on the conditionTypeKey
 * @param {string} conditionTypeKey
 * @param {boolean} isStrengthTrainingOrCardio
 * @param {boolean} showPoolSwim2 // CA-169978: Clean up poolSwim2
 * @returns {string}
 */
const getDurationLabel = (conditionTypeKey, isStrengthTrainingOrCardio, showPoolSwim2) => {
    if (isStrengthTrainingOrCardio) {
        // Strength Training and Cardio workouts use Target for Duration
        return i18next.t('workout:main_target'); // Target
    } else if (showPoolSwim2) {
        // CA-169978: Clean up poolSwim2
        switch (conditionTypeKey) {
            case Types.ConditionTypes.Distance:
                return i18next.t('workout:total_distance');
            case Types.ConditionTypes.Time:
                return i18next.t('workout:total_time');
            case Types.ConditionTypes.Fixed_Rest:
                return i18next.t('workout:fixed_rest_time');
            case Types.ConditionTypes.Reps:
                return i18next.t('workout:main_target');
            case Types.ConditionTypes.Fixed_Repetition:
            case Types.ConditionTypes.Repitition_Time_CSS:
                return i18next.t('workout:send_off_time');
            default:
                return i18next.t('workout:workout_duration');
        }
    } else {
        switch (conditionTypeKey) {
            case Types.ConditionTypes.Distance:
                return i18next.t('workout:total_distance');
            case Types.ConditionTypes.Time:
                return i18next.t('workout:total_time');
            case Types.ConditionTypes.Reps:
                return i18next.t('workout:main_target');
            default:
                return i18next.t('workout:workout_duration');
        }
    }
};

/**
 * Description: Get the string to display for the duration value
 * Note: endCondition is the duration of the workout step (distance/time/lap button/heart rate, etc)
 * Backbone reference: getEndConditionWithValue() in backbone/pages/workouts/util/StepUtil.js
 *
 * @param {ExecutableStepDTO} workoutStep
 * @param {string} segmentSportType
 * @param {string} distanceUnitKey from Personalizer
 * @param {string} estimatedDistanceUnitKey
 * @param {number | null} poolLength
 * @param {number} cssValue
 * @param {boolean} cssFetchFail
 * @param {boolean} showPoolSwim2
 * @returns {string}
 */
const getDurationValue = (
    workoutStep,
    segmentSportType,
    distanceUnitKey,
    estimatedDistanceUnitKey,
    poolLength,
    cssValue,
    cssFetchFail,
    showPoolSwim2 // CA-169978: cleanup poolSwim2 feature flag
) => {
    let durationStr = null;
    const isSwimWorkout = segmentSportType === Types.SportTypes.Swimming;

    if (workoutStep.endCondition && workoutStep.endCondition.conditionTypeKey) {
        // duration
        switch (workoutStep.endCondition.conditionTypeKey) {
            case Types.ConditionTypes.Time:
                durationStr = Personalizer.personalizeDuration(workoutStep.endConditionValue, false, false);
                break;
            case Types.ConditionTypes.Heart_Rate:
                durationStr = workoutStep.endConditionValue + ' ' + i18next.t('units:heart_rate_unit');
                break;
            case Types.ConditionTypes.Lap_Button:
                durationStr = i18next.t('workout:workout.step.duration.type.lap.button');
                break;
            case Types.ConditionTypes.Distance:
                // duration should always be actual, never estimated so hardcode 'false' below for isEstimate
                let distanceUnit;
                if (isSwimWorkout) {
                    // set null when poolLength is Unspecified, other falsy values will not work
                    distanceUnit =
                        workoutStep.preferredEndConditionUnit === null
                            ? null
                            : workoutStep.preferredEndConditionUnit?.unitKey || distanceUnitKey;
                } else {
                    distanceUnit = estimatedDistanceUnitKey ?? distanceUnitKey;
                }

                durationStr = getFormattedDistanceString({
                    distance: workoutStep.endConditionValue,
                    distanceUnit: distanceUnit,
                    isSwimWorkout,
                    isEstimate: false,
                    poolLength,
                    showPoolSwim2 // CA-169978: cleanup poolSwim2 feature flag
                });
                break;
            case Types.ConditionTypes.Calories:
                durationStr = workoutStep.endConditionValue + ' ' + i18next.t('units:label_calories');
                break;
            case Types.ConditionTypes.Power:
                durationStr = workoutStep.endConditionValue + ' ' + i18next.t('units:max_avg_power_unit');
                break;
            case Types.ConditionTypes.Fixed_Rest:
                durationStr = Personalizer.personalizeDuration(workoutStep.endConditionValue, false, false);
                break;
            case Types.ConditionTypes.Fixed_Repetition:
                durationStr = Personalizer.personalizeDuration(workoutStep.endConditionValue, false, false);
                break;
            case Types.ConditionTypes.Reps:
                const reps = workoutStep.endConditionValue + `${workoutStep.isMinReps ? '+' : ''}`;
                durationStr = reps + ' ' + i18next.t('units:label_reps');
                break;
            case Types.ConditionTypes.Repitition_Time_CSS:
                const numStr =
                    workoutStep.endConditionValue > 0
                        ? `+${workoutStep.endConditionValue}`
                        : `${workoutStep.endConditionValue}`;
                const cssInSeconds =
                    Personalizer.convertCriticalSwimSpeed(cssValue || 833) + workoutStep.endConditionValue; // default to 833=2:00 if no CSS exists
                const cssString = cssFetchFail ? '' : `(${Personalizer.personalizeDuration(cssInSeconds)})`;
                const secondsString =
                    workoutStep.endConditionValue === 0 ? `` : `${numStr} ${i18next.t('workout:sec_duration_workout')}`;
                durationStr = `${i18next.t('workout:css')} ${secondsString} ${cssString}`;
                break;
        }
    }

    return durationStr;
};

/**
 * Description: Given a workout step, computes the estimated duration (time or distance)
 * Backbone Reference: getEstimatedDuration() in backbone/pages/workouts/util/StepUtil.js
 * @param {object} workoutStep
 * @param {string} segmentSportType
 * @param {number} speed
 * @param {string} distanceUnitKey
 * @param {number | null} poolLength
 * @param {boolean} showPoolSwim2
 * @returns {{estValue: string, estNumber: number, estLabel: string}}
 */
export const getEstimatedDuration = (
    workoutStep,
    segmentSportType,
    speed,
    distanceUnitKey,
    poolLength,
    showPoolSwim2
) => {
    // CA-169978: cleanup poolSwim2 feature flag
    let estDuration = { estValue: '', estNumber: 0, estLabel: '' };
    let pace = speed;
    const isSwimWorkout = segmentSportType === Types.SportTypes.Swimming;

    // don't show estimates for lap button press duration
    if (workoutStep.endCondition.conditionTypeKey !== Types.DurationTypes.Lap_Button && pace !== 0 && pace !== null) {
        // check if paceForEstimates is 0, don't want to display NaNs or 0s
        // lap button press is never estimated for time or distance
        if (
            workoutStep.targetType &&
            (workoutStep.targetType.workoutTargetTypeKey === Types.TargetTypes.Speed_Zone ||
                workoutStep.targetType.workoutTargetTypeKey === Types.TargetTypes.Pace_Zone)
        ) {
            pace = (workoutStep.targetValueOne + workoutStep.targetValueTwo) / 2;
            if (workoutStep.targetType.workoutTargetTypeKey === Types.TargetTypes.Pace_Zone) {
                pace = getAveragePaceFromIntensityTarget(workoutStep);
            }
        } else if (
            workoutStep.secondaryTargetType &&
            (workoutStep.secondaryTargetType.workoutTargetTypeKey === Types.TargetTypes.Speed_Zone ||
                workoutStep.secondaryTargetType.workoutTargetTypeKey === Types.TargetTypes.Pace_Zone)
        ) {
            pace = (workoutStep.secondaryTargetValueOne + workoutStep.secondaryTargetValueTwo) / 2;
        } else if (workoutStep.stepType.stepTypeKey === Types.StepTypes.Rest) {
            // If no intensity target values (speed or pace) and rest type, don't provide estimates
            return estDuration;
        }

        // poolSwim workouts uses a pace of 2:00 / 100meters for all users regardless of other factors
        // CA-169978: cleanup poolSwim2 feature flag
        if (showPoolSwim2 && isSwimWorkout) {
            pace = 0.8333; // 2:00 / 100meters
        }

        if (workoutStep.endCondition.conditionTypeKey === Types.DurationTypes.Distance) {
            let time = Math.round(workoutStep.endConditionValue / pace);
            time = roundEstimatedTime(time);
            estDuration = {
                estValue: Personalizer.personalizeDuration(time, false, false),
                estNumber: time,
                estLabel: i18next.t('workout:est_time')
            };
        } else {
            const distance = workoutStep.endConditionValue * pace;
            estDuration = {
                estValue: getFormattedDistanceString({
                    distance,
                    distanceUnit: distanceUnitKey,
                    isSwimWorkout,
                    isEstimate: true,
                    poolLength,
                    showPoolSwim2 // CA-169978: cleanup poolSwim2 feature flag
                }),
                estNumber: distance,
                estLabel: i18next.t('workout:est_distance')
            };
        }
    }
    return estDuration;
};

/**
 * Description: Calculate midpoint from pace range rounded to nearest multiple of 5 seconds and return the string as 'm:ss' format
 *   note: rounded value is rounded up to nearest 5 seconds when exactly in the middle of the range
 *
 * @param {number} valueOneSeconds
 * @param {number} valueTwoSeconds
 * @returns {string} Ex: 0:30, 1:05, 10:50
 */
const getPaceMidpointStr = (valueOneSeconds, valueTwoSeconds) => {
    const midpoint = Math.round(Math.round(valueOneSeconds + (valueTwoSeconds - valueOneSeconds) / 2) / 5) * 5;
    const midpointSecs = Math.round(midpoint % 60);
    const midpointMins = Math.floor(midpoint / 60);

    return `${midpointMins}:${midpointSecs < 10 ? '0' + midpointSecs : midpointSecs}`;
};

/**
 * Description: Calculate the midpoint from speed range rounded to nearest multiple of 0.1 mile or km
 * @param {number} valueOne
 * @param {number} valueTwo
 * @returns {string}
 */
const getSpeedMidpointStr = (valueOne, valueTwo) => {
    const midpoint = (valueOne * 100 + (valueTwo * 100 - valueOne * 100) / 2) / 100;
    return Personalizer.formatSpeed(midpoint);
};

/**
 * Description: Get the fields to display for the given target
 * Backbone Reference: getTargetValue() in backbone/pages/workouts/util/StepUtil.js
 *
 * @param {{
 *     workoutStep: *,
 *     sportType: string,
 *     allSportPowerZone: *,
 *     allSportHeartRateZone: *,
 *     isSecondaryTarget: boolean,
 *     powerCurve: *
 *     poolLengthUnitKey: string | null | undefined,
 *     cssValue: number | null,
 *     cssFetchFail: boolean | null
 *     showWorkoutStepMidpoints: boolean // CA-179082: cleanup ticket
 * }} param
 * @returns {{targetValueTwo: *, targetValueOne: string}}
 */
const getTargetValue = ({
    workoutStep,
    sportType,
    allSportPowerZone,
    allSportHeartRateZone,
    isSecondaryTarget,
    powerCurve,
    poolLengthUnitKey,
    cssValue,
    cssFetchFail,
    showWorkoutStepMidpoints // CA-179082: cleanup ticket
}) => {
    const targetType = isSecondaryTarget ? 'secondaryTargetType' : 'targetType';
    const zoneNumberLabel = isSecondaryTarget ? 'secondaryZoneNumber' : 'zoneNumber';
    const firstTarget = isSecondaryTarget ? 'secondaryTargetValueOne' : 'targetValueOne';
    const secondTarget = isSecondaryTarget ? 'secondaryTargetValueTwo' : 'targetValueTwo';
    const targetObject = workoutStep[targetType];
    const zoneNumber = workoutStep[zoneNumberLabel];
    let targetValueOne = workoutStep[firstTarget];
    let targetValueTwo = workoutStep[secondTarget];

    switch (targetObject.workoutTargetTypeKey) {
        case Types.TargetTypes.Pace_Zone:
            targetValueOne = customPersonalizePace(targetValueOne, isSecondaryTarget);
            targetValueTwo = customPersonalizePace(targetValueTwo, isSecondaryTarget);

            // pool swim is always /100m
            if (sportType === Types.SportTypes.Swimming) {
                const unitStr =
                    poolLengthUnitKey === 'meter'
                        ? i18next.t('units:distance.meter')
                        : poolLengthUnitKey === 'yard'
                        ? i18next.t('units:distance.yard')
                        : '';
                // if pool size is in yards convert the value to meters, device always expects meters and API doesn't do a conversion on this field
                const paceDistance = poolLengthUnitKey === 'yard' ? 100 * ConversionFactors.YardsToMeters : 100;
                const mins = Math.floor(Math.round(paceDistance / workoutStep[firstTarget]) / 60);
                const secs = Math.floor(Math.round(paceDistance / workoutStep[firstTarget]) % 60);
                targetValueOne = `${mins}:${secs < 10 ? '0' + secs : secs} /100${unitStr}`;
            } else {
                // CA-179082: cleanup ticket
                if (showWorkoutStepMidpoints) {
                    const speedOne = Personalizer.convertSpeed(workoutStep[firstTarget]);
                    const secondsOne = Math.round(3600 / speedOne);
                    const speedTwo = Personalizer.convertSpeed(workoutStep[secondTarget]);
                    const secondsTwo = Math.round(3600 / speedTwo);
                    const unit = isMetric()
                        ? i18next.t('units:pace_unit_metric')
                        : i18next.t('units:pace_unit_statute');

                    targetValueOne =
                        getPaceMidpointStr(secondsOne, secondsTwo) +
                        ' ' +
                        unit +
                        '<br>(' +
                        targetValueOne +
                        '-' +
                        targetValueTwo +
                        ' ' +
                        unit +
                        ')';
                } else {
                    if (isMetric()) {
                        targetValueOne += '-' + targetValueTwo + ' ' + i18next.t('workout:workout_pace_unit_metric');
                    } else {
                        targetValueOne += '-' + targetValueTwo + ' ' + i18next.t('workout:workout_pace_unit_statute');
                    }
                }
            }
            targetValueTwo = null;
            break;
        case Types.ChildTargetTypes.Select_Power_Zone:
        case Types.TargetTypes.Power_Zone:
            const powerZone = getZoneBySportType(allSportPowerZone, sportType);
            if (!!zoneNumber && zoneNumber > 0) {
                const zoneValuesSetFromBiometrics = getZonesValues(zoneNumber, powerZone);
                targetValueOne = zoneValuesSetFromBiometrics.targetValueOne;
                targetValueTwo = zoneValuesSetFromBiometrics.targetValueTwo;
            }
            if (zoneNumber === getPowerZoneCount(sportType) && targetValueOne !== null) {
                // if last zone of power zones
                // (we receive the same values from the API on the last zone when no next values is available)
                // Then force set to null so it wil show (Over 300 W) instead of (300 of 300 W)
                targetValueTwo = null;
            }
            // is null if custom power zone - but can be 0

            if (
                !zoneNumber &&
                !isNaN(targetValueOne) &&
                !isNaN(targetValueTwo) &&
                targetValueOne !== null &&
                targetValueTwo !== null
            ) {
                let targetValueUnitKey = isSecondaryTarget ? 'secondaryTargetValueUnit' : 'targetValueUnit';
                let isAnFtpValue = workoutStep[targetValueUnitKey] && workoutStep[targetValueUnitKey].unitKey;
                let functionalThresholdPower = powerZone && powerZone.functionalThresholdPower;

                if (isAnFtpValue && functionalThresholdPower && !isNaN(functionalThresholdPower)) {
                    targetValueOne =
                        targetValueOne +
                        '%-' +
                        targetValueTwo +
                        i18next.t('workout:power_zone_percent_FTP') +
                        '</br>' +
                        Math.floor((functionalThresholdPower / 100) * targetValueOne) +
                        '-' +
                        Math.floor((functionalThresholdPower / 100) * targetValueTwo) +
                        ' ' +
                        i18next.t('units:max_avg_power_unit');
                } else {
                    targetValueOne =
                        targetValueOne + '-' + targetValueTwo + ' ' + i18next.t('units:max_avg_power_unit');
                }
            } else {
                targetValueOne = getTargetZoneFieldString(targetValueOne, targetValueTwo, zoneNumber, true);
            }
            targetValueTwo = null;
            break;
        case Types.ChildTargetTypes.Select_Heart_Rate_Zone:
        case Types.TargetTypes.Heart_Rate_Zone:
            const heartRateZone = getZoneBySportType(allSportHeartRateZone, sportType);
            if (zoneNumber !== null && zoneNumber > 0) {
                //set the HR zones from the biometrics - because the values from the api are persisted even if the user changes the zones - and API will remove the persisted values
                // if the zone is null than it is a custom HR
                const HRValuesSetFromBiometrics = getZonesValues(zoneNumber, heartRateZone);
                targetValueOne = HRValuesSetFromBiometrics.targetValueOne;
                targetValueTwo = HRValuesSetFromBiometrics.targetValueTwo;
            }
            // zone number will be null if custom
            if (
                zoneNumber === null &&
                !isNaN(targetValueOne) &&
                !isNaN(targetValueTwo) &&
                targetValueOne !== null &&
                targetValueTwo !== null
            ) {
                targetValueOne = targetValueOne + '-' + targetValueTwo + ' ' + i18next.t('units:heart_rate_unit');
            } else {
                targetValueOne = getTargetZoneFieldString(targetValueOne, targetValueTwo, zoneNumber, false);
            }
            targetValueTwo = null;
            break;
        case Types.TargetTypes.Speed_Zone:
            // CA-179082: cleanup ticket - remove the else statement and it's contents when removing the feature flag
            if (showWorkoutStepMidpoints) {
                targetValueOne = Personalizer.personalizeSpeed(targetValueOne, 'cycling', null);
                targetValueTwo = Personalizer.personalizeSpeed(targetValueTwo, 'cycling', null);
                const speedUnit = isMetric()
                    ? i18next.t('units:speed_unit_metric')
                    : i18next.t('units:speed_unit_statute');
                const midpoint = getSpeedMidpointStr(+targetValueOne, +targetValueTwo);
                targetValueOne = `${midpoint} ${speedUnit}<br>(${targetValueOne}-${targetValueTwo} ${speedUnit})`;
            } else {
                targetValueOne = Personalizer.personalizeSpeed(targetValueOne, 'cycling', null);
                targetValueTwo = Personalizer.personalizeSpeed(targetValueTwo, 'cycling', null);
                const speedUnit = isMetric()
                    ? i18next.t('units:speed_unit_metric')
                    : i18next.t('units:speed_unit_statute');
                targetValueOne = targetValueOne + '-' + targetValueTwo + ' ' + speedUnit;
            }

            targetValueTwo = null;
            break;
        case Types.TargetTypes.Cadence:
            targetValueOne = Personalizer.personalizeCadence(targetValueOne);
            targetValueTwo = Personalizer.personalizeCadence(targetValueTwo);
            const cadenceUnit =
                sportType === Types.SportTypes.Cycling
                    ? i18next.t('units:bike_cadence_unit')
                    : i18next.t('units:run_cadence_unit');
            targetValueOne = targetValueOne + '-' + targetValueTwo + ' ' + cadenceUnit;
            targetValueTwo = null;
            break;
        case Types.TargetTypes.Effort_Based:
            const options = getEffortBasedOptions();
            const effort = options.find((opt) => opt.value === targetValueOne);
            targetValueOne = effort?.textKey;
            targetValueTwo = null; // valueTwo is not used in UI
            break;
        case Types.TargetTypes.Power_Curve:
            let powerCurvePercentageTarget, powerCurveScale, powerCurveDuration;
            if (powerCurve && powerCurve['entries'] && powerCurve['entries'].length) {
                powerCurveDuration = isSecondaryTarget
                    ? workoutStep.secondaryTargetPowerCurveDuration
                    : workoutStep.powerCurveDuration;
                powerCurveScale = isSecondaryTarget
                    ? workoutStep.secondaryTargetPowerCurveScale
                    : workoutStep.powerCurveScale;
                const powerCurveTarget = powerCurve['entries'].filter(function(entry) {
                    return entry.duration === +powerCurveDuration;
                })[0];
                powerCurvePercentageTarget = powerCurveTarget?.targetDropdownValues.filter(function(target) {
                    return target.percentage === +powerCurveScale;
                })[0];
            }

            targetValueOne = powerCurvePercentageTarget ? powerCurvePercentageTarget.label : powerCurveScale + '%';
            targetValueTwo = null;
            break;
        case Types.TargetTypes.CSS_Based_Target_Pace:
            const numStr = targetValueOne > 0 ? `+${targetValueOne}` : `${targetValueOne}`;
            const cssInSeconds = Personalizer.convertCriticalSwimSpeed(cssValue || 833) + targetValueOne; // default to 833=2:00 if no CSS exists
            const cssString = cssFetchFail ? '' : `(${Personalizer.personalizeDuration(cssInSeconds)})`;
            const secondsString = targetValueOne === 0 ? `` : `${numStr} ${i18next.t('workout:sec_duration_workout')}`;
            targetValueOne = `${i18next.t('workout:css')} ${secondsString} ${cssString}`;
            break;
        default:
            break;
    }

    return { targetValueOne, targetValueTwo };
};

/**
 * Description: Get the average pace from target values
 * @param {object} workoutStep
 * @returns {number}
 */
const getAveragePaceFromIntensityTarget = (workoutStep) => {
    let minPace = 0;
    let maxPace = 0;
    let targetType = null;

    minPace = workoutStep.targetValueOne;
    maxPace = workoutStep.targetValueTwo;
    targetType = workoutStep.targetType.workoutTargetTypeKey;

    minPace = Personalizer.convertIntensityTargetPace(minPace);
    maxPace = Personalizer.convertIntensityTargetPace(maxPace);

    let avgPace = (minPace + maxPace) / 2;

    avgPace = Personalizer.revertIntensityTargetPace(avgPace);

    return avgPace;
};

/**
 * Description: Determine if the workout step should show the stroke field
 * Backbone Reference: getStepValues() in backbone/pages/workouts/util/StepUtil.js
 * @param {object} workoutStep
 * @returns {boolean}
 */
const showStrokeType = (workoutStep) => {
    return workoutStep.strokeType && workoutStep.strokeType.strokeTypeKey;
};

/**
 * Description: Determine if the workout step should show the estimated distance/time field
 * Backbone Reference: getStepValues() in backbone/pages/workouts/util/StepUtil.js
 *
 * @param {object} workoutStep
 * @param {string} segmentSportType
 * @param {boolean} showPoolSwim2 // CA-169978: remove showPoolSwim2 arg once the feature is released and works well
 * @returns {boolean}
 */
const showEstimateField = (workoutStep, segmentSportType, showPoolSwim2) => {
    // CA-169978: remove showPoolSwim2 once the feature is released and works well
    return showPoolSwim2
        ? // for pool swim only show estimated distance, estimated time is removed
          (segmentSportType === Types.SportTypes.Swimming &&
              workoutStep.endCondition?.conditionTypeKey === Types.DurationTypes.Time) ||
              // for non pool swim only show estimates for these sport types
              ([Types.SportTypes.Running, Types.SportTypes.Cycling].includes(segmentSportType) &&
                  // only duration types of distance & time are estimated for non pool swim sport types
                  [Types.DurationTypes.Distance, Types.DurationTypes.Time].includes(
                      workoutStep.endCondition?.conditionTypeKey
                  ))
        : // for non pool swim only show estimates for these sport types
          [Types.SportTypes.Running, Types.SportTypes.Cycling, Types.SportTypes.Swimming].includes(segmentSportType) &&
              // only duration types of distance & time are estimated for non pool swim sport types
              [Types.DurationTypes.Distance, Types.DurationTypes.Time].includes(
                  workoutStep.endCondition?.conditionTypeKey
              );
};

/**
 * Description: Determine if the workout step should show the exercise field
 * Backbone Reference: shouldAddExerciseToStepType() and shouldAddAlternativeExerciseName() in backbone/pages/workouts/util/StepUtil.js
 * @param {object} segmentSportType
 * @param {object} workoutStep
 * @returns {boolean}
 */
const showExerciseField = (segmentSportType, workoutStep) => {
    if (workoutStep.stepType.stepTypeKey === Types.StepTypes.Rest) {
        return false;
    }
    if (
        segmentSportType === Types.SportTypes.Strength_Training ||
        segmentSportType === Types.SportTypes.Cardio ||
        segmentSportType === Types.SportTypes.Hiit ||
        segmentSportType === Types.SportTypes.Yoga ||
        segmentSportType === Types.SportTypes.Pilates ||
        segmentSportType === Types.SportTypes.Mobility
    ) {
        return true;
    }
    return false;
};

/**
 * Description: Determine if the workout step should show the equipment field
 * Backbone Reference: getStepValues() in backbone/pages/workouts/util/StepUtil.js
 * @param {object} workoutStep
 * @returns {boolean}
 */
const showEquipmentField = (workoutStep) => {
    return workoutStep.equipmentType && workoutStep.equipmentType.equipmentTypeKey;
};

/**
 * Description: Determine if the workout step should show the intensity target field
 * Backbone Reference: getStepValues() in backbone/pages/workouts/util/StepUtil.js
 *
 * @param {object} workoutStep
 * @returns {boolean}
 */
const showIntensityTargetField = (workoutStep) => {
    return (
        workoutStep.targetType &&
        workoutStep.targetType.workoutTargetTypeKey &&
        workoutStep.targetType.workoutTargetTypeKey !== Types.TargetTypes.No_Target
    );
};

/**
 * Description: Determine if the workout step should show the secondary target field
 * Backbone Reference: getStepValues() in backbone/pages/workouts/util/StepUtil.js
 *
 * @param {object} workoutStep
 * @returns {boolean}
 */
const showSecondaryTargetField = (workoutStep) => {
    return (
        workoutStep.secondaryTargetType &&
        workoutStep.secondaryTargetType.workoutTargetTypeKey &&
        workoutStep.secondaryTargetType.workoutTargetTypeKey !== Types.TargetTypes.No_Target
    );
};

/**
 * Description: Determine if the workout step should show the drill field
 * @param {object} workoutStep
 * @returns {boolean}
 */
const showDrillField = (workoutStep, showPoolSwim2) => {
    // CA-169978: remove showPoolSwim2 once the feature's released and working
    return showPoolSwim2 && workoutStep.drillType && workoutStep.drillType.drillTypeKey;
};

/**
 * Description: Determine if the workout step should show the weight field
 * @param {string} segmentSportType
 * @param {ExecutableStepDTO} workoutStep
 * @param {boolean} isViewingProviderWorkoutPage
 * @returns
 */
const showWeightField = (segmentSportType, workoutStep, isViewingProviderWorkoutPage) => {
    if (isViewingProviderWorkoutPage) {
        return false;
    }
    if (workoutStep.stepType.stepTypeKey === Types.StepTypes.Rest) {
        return false;
    }
    return [Types.SportTypes.Strength_Training, Types.SportTypes.Cardio, Types.SportTypes.Hiit].includes(
        segmentSportType
    );
};

/**
 * Description: Build step with default end condition
 * Backbone Reference: getEmptyStepViewDataBasedOnSportType() in backbone/pages/workouts/WoroutPageView.js
 * @param {string} sportTypeKey
 * @param {string} poolLengthUnitKey
 * @param {string | null | undefined} [stepTypeKey] // this sets stepType.stepTypeKey
 * @param {string | null | undefined} [customTypeKey] // this sets endCondition.conditionTypeKey
 * @param {object | null | undefined} [overrideFields]
 * @returns  {object}
 */
export const buildStepDataWithEndConditions = (
    sportTypeKey,
    poolLengthUnitKey,
    stepTypeKey,
    customTypeKey,
    overrideFields
) => {
    const endConditions = {};
    let appendObject = {};
    stepTypeKey = stepTypeKey ? stepTypeKey : Types.StepTypes.Interval;

    switch (sportTypeKey) {
        //other or custom behaves as running
        case Types.SportTypes.Other:
            endConditions.typeKey = Types.DurationTypes.Time;
            endConditions.value = 300;
            break;
        case Types.SportTypes.Running:
            const isMetric = Personalizer.isMetricMeasurementSystem();
            const unit = isMetric ? 'kilometer' : 'mile';
            endConditions.typeKey = Types.DurationTypes.Distance;
            endConditions.value = Conversion.convertToMeters(unit, 1);
            break;
        case Types.SportTypes.Cycling:
            endConditions.typeKey = Types.DurationTypes.Time;
            endConditions.value = 1200;
            break;
        case Types.SportTypes.Swimming:
            endConditions.typeKey = Types.DurationTypes.Distance;
            endConditions.value = 200;
            break;
        case Types.SportTypes.Yoga:
            endConditions.typeKey = Types.DurationTypes.Time;
            endConditions.value = 10;
            break;
        case Types.SportTypes.Pilates:
        case Types.SportTypes.Mobility:
            //this should cover pilates
            endConditions.typeKey = Types.DurationTypes.Reps;
            endConditions.value = 10;
            break;
        case Types.SportTypes.Hiit:
            endConditions.typeKey = Types.DurationTypes.Time;
            endConditions.value = stepTypeKey === Types.StepTypes.Rest ? 10 : 30;
            break;
        case Types.SportTypes.Cardio:
            // Exercise for 10 reps or Rest for 1 min
            endConditions.typeKey = Types.DurationTypes.Reps;
            endConditions.value = stepTypeKey === Types.StepTypes.Rest ? 60 : 10;
            break;
        default:
            //this should cover strength training
            endConditions.typeKey = Types.DurationTypes.Reps;
            endConditions.value = 10;
            break;
    }

    if (customTypeKey) {
        //to overWrite the DurationTypes typeKey if it is needed
        endConditions.typeKey = customTypeKey;
    }

    if (endConditions.typeKey) {
        appendObject = {
            ...appendObject,
            endCondition: { conditionTypeKey: endConditions.typeKey },
            endConditionValue: endConditions.value
        };
    }

    // overrideFields will take priority over appendedObject and any other fields
    return buildStepData(sportTypeKey, poolLengthUnitKey, stepTypeKey, { ...appendObject, ...overrideFields });
};

/**
 * Description: build step with optional appending data
 * Backbone Reference: getEmptyStep() in backbone/pages/workouts/WoroutPageView.js
 * @param {string} sportTypeKey
 * @param {string} poolLengthUnitKey
 * @param {string | null} stepTypeKey
 * @param {object | null} [append]
 * @returns {object}
 */
export const buildStepData = (sportTypeKey, poolLengthUnitKey, stepTypeKey, append) => {
    let defaults = {
        stepId: null,
        stepOrder: -1,
        stepType: {
            stepTypeKey: stepTypeKey || Types.StepTypes.Interval
        },
        type: ExecutableStepDTOType
    };

    // add swimming specific default data
    if (sportTypeKey === Types.SportTypes.Swimming) {
        if (stepTypeKey !== Types.StepTypes.Rest) {
            defaults['preferredEndConditionUnit'] = {
                unitId: null,
                unitKey: poolLengthUnitKey
            };
            defaults['strokeType'] = {
                strokeTypeId: 1,
                strokeTypeKey: 'any_stroke'
            };
        }
        defaults['equipmentType'] = {
            equipmentTypeId: 0,
            equipmentTypeKey: null
        };
    } else if (sportTypeKey === Types.SportTypes.Strength_Training) {
        // add strength training specific default data
        if (stepTypeKey === Types.StepTypes.Warm_Up) {
            // use "Cardio" as the default Warm Up exercise
            defaults['category'] = 'CARDIO';
            defaults['exerciseName'] = 'CARDIO';
        }
    }

    if (append) {
        // append fields will take priority over defaults
        return { ...defaults, ...append };
    }
    return defaults;
};

/**
 * Backbone Reference: getEmptyRepeatStep() in backbone/pages/workouts/WorkoutPageView.js
 * @param {object[]} workoutSteps
 * @param {string} sportTypeKey
 * @param {object | null | undefined} [overrideFields]
 * @returns {object}
 */
export const buildRepeatStepData = (workoutSteps, sportTypeKey, overrideFields) => {
    // child step id only used to check whether a step is inside a repeat step in web
    // hardcode 1 to maintain its minimum functionality
    // this doesn't affect api since api will generate itself
    workoutSteps.forEach((step) => (step.childStepId = 1));
    let numberOfIterations = getWorkoutConfiguration(sportTypeKey).WorkoutOptions.DefaultRepeatIteration;
    const defaultFields = {
        stepId: null,
        stepOrder: -1,
        stepType: {
            stepTypeKey: Types.StepTypes.Repeat
        },
        numberOfIterations,
        smartRepeat: false,
        childStepId: 1,
        workoutSteps,
        endCondition: { conditionTypeKey: Types.ConditionTypes.Iterations },
        type: RepeatGroupDTOType
    };

    // overrideFields takes priority over defaultFields
    return { ...defaultFields, ...overrideFields };
};

/**
 * Backbone Reference: checkWhereToAddStep() in backbone/pages/workouts/WorkoutPageView.js
 * When current workout
 * 1. contains only one cooldown step
 * 2. based on 1., the cooldown step is the final one
 * 3. based on 1. & 2., the cooldown step is not inside a repeater block
 * we should add the new step/repeat right before the cooldown step (return false)
 * @param {object[]} workoutSteps
 * @returns {boolean}
 */
export const shouldAppendStepToLast = (workoutSteps, isInRepeatStep = false) => {
    let coolDownCount = 0;

    for (const step of workoutSteps) {
        // case 3
        if (step.stepType.stepTypeKey === Types.StepTypes.Cool_Down && isInRepeatStep) {
            return true;
        }
        // case 3
        if (step.stepType.stepTypeKey === Types.StepTypes.Repeat && shouldAppendStepToLast(step.workoutSteps, true)) {
            return true;
        }
        if (step.stepType.stepTypeKey === Types.StepTypes.Cool_Down) {
            coolDownCount++;
        }
        // case 1
        if (coolDownCount > 1) {
            return true;
        }
    }

    // continue to check outer steps
    if (isInRepeatStep) {
        return false;
    }
    // case 2
    const isCooldownLastStep = workoutSteps.at(-1)?.stepType.stepTypeKey === Types.StepTypes.Cool_Down;
    return !(coolDownCount === 1 && isCooldownLastStep);
};

export const getFieldsToDisplay = (sportType, stepType, showPoolSwim2) => {
    switch (sportType) {
        case Types.SportTypes.Swimming:
            return getSwimmingFields(stepType, showPoolSwim2);
        case Types.SportTypes.Running:
            return getRunningFields(stepType);
        case Types.SportTypes.Cycling:
            return getCyclingFields(stepType);
        case Types.SportTypes.Strength_Training:
            return getStrengthTrainingFields(stepType);
        case Types.SportTypes.Hiit:
            return getHiitFields(stepType);
        case Types.SportTypes.Yoga:
            return getYogaFields(stepType);
        case Types.SportTypes.Pilates:
        case Types.SportTypes.Mobility:
            return getPilatesFields(stepType);
        case Types.SportTypes.Cardio:
            return getCardioFields(stepType);
        case Types.SportTypes.Other:
        default:
            return getOtherWorkoutFields(stepType);
    }
};

const getCardioFields = (stepType) => {
    if (stepType === Types.StepTypes.Rest) {
        return [StepFields.Duration];
    } else {
        return [StepFields.Exercises, StepFields.Duration, StepFields.Weight];
    }
};
// CA-169978: remove showPoolSwim2 once the feature's released and working
const getSwimmingFields = (stepType, showPoolSwim2) => {
    if (stepType === Types.StepTypes.Rest) {
        return [StepFields.Duration];
    } else {
        // CA-169978: remove showPoolSwim2 once the feature's released and working
        return showPoolSwim2
            ? [
                  StepFields.Duration,
                  StepFields.StrokeType,
                  StepFields.Drill,
                  StepFields.Equipment,
                  StepFields.SecondaryTarget
              ]
            : [StepFields.Duration, StepFields.StrokeType, StepFields.Equipment];
    }
};

const getRunningFields = (stepType) => {
    return [StepFields.Duration, StepFields.IntensityTarget];
};

const getCyclingFields = (stepType) => {
    return [StepFields.Duration, StepFields.IntensityTarget, StepFields.SecondaryTarget];
};

const getStrengthTrainingFields = (stepType) => {
    if (stepType === Types.StepTypes.Rest) {
        return [StepFields.Duration];
    } else {
        return [StepFields.Exercises, StepFields.Duration, StepFields.Weight];
    }
};

const getHiitFields = (stepType) => {
    if (stepType === Types.StepTypes.Rest) {
        return [StepFields.Duration, StepFields.IntensityTarget];
    } else {
        return [StepFields.Exercises, StepFields.Duration, StepFields.Weight, StepFields.IntensityTarget];
    }
};

const getYogaFields = (stepType) => {
    if (stepType === Types.StepTypes.Rest) {
        return [StepFields.Duration];
    }
    return [StepFields.Exercises, StepFields.Duration];
};

const getPilatesFields = (stepType) => {
    if (stepType === Types.StepTypes.Rest) {
        return [StepFields.Duration];
    } else {
        return [StepFields.Exercises, StepFields.Duration];
    }
};

const getOtherWorkoutFields = (stepType) => {
    return [StepFields.Duration, StepFields.IntensityTarget];
};

/**
 * Description: Clear fields from stepData that are not supported by the new step type for the given sport type and add any defaults or keep existing fields based on sport/step requirements
 *
 * @param {string} sportType
 * @param {string} newStepType
 * @param {object} stepData
 * @returns {object}
 */
export const updateStepFieldsOnStepTypeChange = (workoutData, sportType, newStepType, stepData) => {
    let newStepData = stepData;

    switch (sportType) {
        case Types.SportTypes.Swimming:
            newStepData = updateSwimmingFields(workoutData, newStepType, stepData);
            break;
        case Types.SportTypes.Hiit:
        case Types.SportTypes.Yoga:
        case Types.SportTypes.Pilates:
        case Types.SportTypes.Cardio:
        case Types.SportTypes.Strength_Training:
        case Types.SportTypes.Mobility:
            newStepData = updateExerciseFields(newStepType, stepData);
            break;
    }

    return newStepData;
};

/**
 * Description: update step fields for step type, removing uneeded fields and adding defaults where data doesn't already exist
 *
 * @param {string} stepType
 * @param {object} stepData
 * @returns {object}
 */
const updateExerciseFields = (stepType, stepData) => {
    let newStepData = { ...stepData };

    switch (stepType) {
        case Types.StepTypes.Rest:
            newStepData.exerciseName = null;
            if (newStepData.category) {
                delete newStepData.category;
            }
            break;
    }

    return newStepData;
};

/**
 * Description: update step fields for step type, removing uneeded fields and adding defaults where data doesn't already exist
 *
 * @param {string} stepType
 * @param {object} stepData
 * @returns {object}
 */
const updateSwimmingFields = (workoutData, stepType, stepData) => {
    let newStepData = stepData;

    switch (stepType) {
        case Types.StepTypes.Rest:
            // clear data and set defaults when changing stepType to Rest
            newStepData.drillType = null;
            newStepData.endCondition = { conditionTypeKey: Types.ConditionTypes.Lap_Button };
            newStepData.endConditionValue = null;
            newStepData.strokeType = { strokeTypeKey: null };
            newStepData.equipmentType = { equipmentTypeKey: null };
            newStepData.targetType = null;
            newStepData.targetValueOne = null;
            newStepData.targetValueTwo = null;
            newStepData.targetValueUnit = null;
            newStepData.secondaryTargetType = null;
            newStepData.secondaryTargetValueOne = null;
            newStepData.secondaryTargetValueTwo = null;
            newStepData.secondaryTargetValueUnit = null;
            newStepData.secondaryZoneNumber = null;
            break;
        case Types.StepTypes.Main:
        case Types.StepTypes.Interval:
        case Types.StepTypes.Warm_Up:
        case Types.StepTypes.Cool_Down:
            // if previous stepType was Rest set defaults for new stepType
            if (stepData.stepType.stepTypeKey === Types.StepTypes.Rest) {
                newStepData.endCondition = { conditionTypeKey: Types.ConditionTypes.Distance };
                newStepData.endConditionValue = +workoutData.poolLength;
                newStepData.strokeType = {
                    strokeTypeKey: newStepData.strokeType?.strokeTypeKey || Types.StrokeTypes.Free
                };
            }
            break;
    }

    return newStepData;
};

/**
 * Description: Recurively iterate through rest steps and update every executable step passed in with the updateFunc provided
 *
 * @param {Array<object>} steps
 * @param {Function} updateFunc
 * @returns {Array<object>}
 */
export const updateStepsRecursively = (steps, updateFunc) => {
    return steps.map((step) => {
        // recurse through repeat blocks
        if (step.workoutSteps?.length > 0) {
            return { ...step, workoutSteps: updateStepsRecursively(step.workoutSteps, updateFunc) };
        }

        // update the current stepData with the given function
        return updateFunc(step);
    });
};

/**
 * get manual weight set value
 * Backbone Reference: displayWeightInput in backbone/pages/workouts/dropdowns/BenchmarkScalingWeightDropdown.js
 * @param {ExecutableStepDTO} workoutStep
 * @returns
 */
export const getManualWeightValue = (workoutStep) => {
    let weightValue, stepUnitKey;
    if (workoutStep?.weightUnit) {
        stepUnitKey =
            workoutStep.weightUnit.unitKey === 'pound'
                ? Personalizer.WeightUnitKeys.STATUTE
                : Personalizer.WeightUnitKeys.METRIC;
    }
    if (workoutStep?.weightValue) {
        weightValue =
            stepUnitKey === Personalizer.getWeightUnitKey('')
                ? Personalizer.personalizeWeightWithoutConvert(workoutStep.weightValue, '')
                : Personalizer.personalizeWeight(
                      Personalizer.convertWeightToGramsBySystem(workoutStep.weightValue, stepUnitKey),
                      '',
                      ''
                  );
    }
    return weightValue;
};

/**
 * Backbone Reference: getTargetValue() in backbone/pages/workouts/util/StepUtil.js
 * get weight display value and label
 * @param {ExecutableStepDTO} workoutStep
 * @param {string} segmentSportType
 * @param {array} exerciseTypes
 * @param {array} benchmarkExercises
 * @returns {{value: string|number, label: string}}
 */
const getWeightFieldValue = (workoutStep, segmentSportType, exerciseTypes = [], benchmarkExercises = []) => {
    // For strengh training exercises with benchmarks associated, we will display the computed weight
    if (
        workoutStep.benchmarkKey &&
        workoutStep.benchmarkPercentage &&
        benchmarkExercises &&
        benchmarkExercises.length > 0
    ) {
        const linkedBenchmarkExercise = getLinkedBenchmarkExercise(
            workoutStep.exerciseName,
            workoutStep.category,
            benchmarkExercises
        );
        if (linkedBenchmarkExercise) {
            const weightWithUnit = getWeightWithUnit(
                linkedBenchmarkExercise.oneRepMax,
                linkedBenchmarkExercise.weightUnit.unitKey
            );
            return {
                value: getWeightWithUnit(
                    (linkedBenchmarkExercise.oneRepMax * workoutStep.benchmarkPercentage) / 100,
                    linkedBenchmarkExercise.weightUnit.unitKey
                ),
                label: i18next.t('units:label_weight_percent_of', {
                    0: workoutStep.benchmarkPercentage,
                    1: weightWithUnit
                })
            };
        }
    } else if (workoutStep.weightUnit && workoutStep.weightValue && workoutStep.weightValue > 0) {
        return {
            value: `${getManualWeightValue(workoutStep)} ${i18next.t(`units:${Personalizer.getWeightUnitKey('')}`)}`,
            label: i18next.t('units:label_weight')
        };
    } else if ([Types.SportTypes.Hiit, Types.SportTypes.Strength_Training].includes(segmentSportType)) {
        const stepExerciseConfig = exerciseTypes.find(
            (exercise) =>
                exercise.exerciseKey === workoutStep.exerciseName && exercise.categoryKey === workoutStep.category
        );
        if (stepExerciseConfig && stepExerciseConfig.isBodyWeight) {
            return {
                value: i18next.t('units:label_weight_body'),
                label: i18next.t('units:label_weight')
            };
        }
    }
    return {
        value: '',
        label: ''
    };
};

/**
 * pool swim workout contains a step with pace or send off time
 * @param {ExecutableStepDTO} step
 * @returns {boolean}
 */
export const containsPaceOrSendOff = (step) => {
    return (
        step.secondaryTargetType?.workoutTargetTypeKey === Types.TargetTypes.Pace_Zone ||
        step.endCondition?.conditionTypeKey === Types.ConditionTypes.Fixed_Repetition
    );
};
